<template>
<m-pageframe
    title="Search Organizations"
>
<v-container fluid style="padding-left: 30px; padding-right: 30px; padding-top: 30px;">


     <v-text-field
         label="Search"
         prepend-icon="mdi-magnify"
         v-model="search"
         v-on:input="update"
         autofocus
         style="margin-bottom: 15px; margin-top: -15px;"
         autocomplete="off"
     >
         <v-menu
             bottom
             offset-y
             slot="append-outer"
         >
             <template v-slot:activator="{ on }">
                 <v-icon
                     v-on="on"
                 >
                     mdi-dots-vertical
                 </v-icon>
             </template>
     
             <v-list>
                 <v-list-item
                     v-on:click="onClickExportToExcel()"
                 >
                     <v-list-item-title>Export to Excel...</v-list-item-title>
                 </v-list-item>

                 <v-divider/>

                 <v-list-item
                     v-on:click="onClickSnapshotWelcomeTasks()"
                 >
                     <v-list-item-title>Refresh ToDo Counts</v-list-item-title>
                 </v-list-item>

                 <v-divider/>

                 <v-list-item
                     v-on:click="onClickNewProspectiveOrganization()"
                 >
                     <v-list-item-title>New prospective organization...</v-list-item-title>
                 </v-list-item>
             </v-list>
         </v-menu> 
     </v-text-field>

      <m-edit-tag-filter
            label="Tags"
            applies_to="organization"
            v-model="tag_filter"
            v-on:input="doUpdate"
            style="margin-top: -15px; margin-bottom: 25px;"
        />

     <v-row>

        <v-col>
            <v-select
                label="Industry"
                :items="$rc.enumChoices('industry_code')"
                v-model="search_industry"
                v-on:change="doUpdate()"
            />
        </v-col>

        <v-col>
            <v-select
                label="State"
                :items="$rc.enumChoices('organization_state')"
                v-model="search_state"
                v-on:change="doUpdate()"
            />
        </v-col>

        <v-col>
            <v-select
                label="Integration Enabled"
                :items="this.$rc.enumChoices('integration_name')"
                v-model="search_integration"
                v-on:change="doUpdate()"
            />
        </v-col>

       


     </v-row>

     

    <v-layout row wrap>
        <v-flex flex-grow-0>
            <m-boolean-search-filter
                style="margin-top: 5px; margin-left: 10px;"
                label="Prospective"
                v-model="filter_prospective_organizations"
                v-on:input='doUpdate'
            />
        </v-flex>

        <v-flex flex-grow-0>
            <m-boolean-search-filter
                style="margin-top: 5px;"
                label="Archived"
                v-model="filter_archived_accounts"
                v-on:input='doUpdate'
            />
        </v-flex>

        <v-flex flex-grow-0>
            <m-boolean-search-filter
                style="margin-top: 5px;"
                label="Revenue"
                v-model="filter_non_revenue_accounts"
                v-on:input='doUpdate'
            />
        </v-flex>

    </v-layout>

    <v-data-table 
        :headers="headers"
        :items="results"
        :items-per-page="100"
        class="elevation-1"
        hide-default-footer
        :custom-sort="customSort"
        :sort-by.sync="sort_by"
        :sort-desc.sync="sort_desc"
    >
        <template v-slot:item.tags="{ item }"> 
            <m-rc-tag-display
                :value="item.rc_tags"
            />
        </template>

        <template v-slot:item.label="{ item }"> 
           <span :style="styleLabel(item)">{{item.label}}</span>
        </template>

        <template v-slot:item.time_training_date="{ item }"> 
           {{$rc.pretty(item.time_training, 'date')}}
        </template>

        <template v-slot:item.time_training_time="{ item }"> 
           {{$rc.pretty(item.time_training, 'time')}}
        </template>
        
        <template v-slot:item.is_archived="{ item }">
            <v-simple-checkbox :value="item.is_archived" disabled></v-simple-checkbox>
        </template>

        <template v-slot:item.is_prospective_organization="{ item }">
            <v-simple-checkbox :value="item.is_prospective_organization" disabled></v-simple-checkbox>
        </template>

        <template v-slot:item.is_revenue_account="{ item }">
            <v-simple-checkbox :value="!item.is_revenue_account" disabled></v-simple-checkbox>
        </template>

        <template v-slot:item.red_chirp_promotion_prohibited="{ item }">
            <v-simple-checkbox :value="item.red_chirp_promotion_prohibited" disabled></v-simple-checkbox>
        </template>

        <template v-slot:item.vetting_status="{ item }">
            <span v-html="$rc.prettyTCRVettingStatus(item.vetting_status)"></span>
        </template>

        <template v-slot:item.time_campaign_created="{ item }">
            {{$rc.pretty(item.time_campaign_created, 'date')}}
        </template>
        

        <template v-slot:item.integrations="{ item }">
            {{$rc.pretty(item.active_integrations, 'comma_list')}}
        </template>
        

        <template v-slot:item.action="{ item }">
            <v-menu 
                bottom 
                offset-y
            >

                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-dots-vertical-circle-outline
                    </v-icon>
                </template>

                <v-list>

                    <v-list-item
                        v-on:click="onClickViewNotes(item)"
                    >
                        <v-list-item-title>View / edit notes...</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickProvisionScreen(item)"
                        :disabled="item.organization_state == 'prospect'"
                    >
                        <v-list-item-title>Provision screen...</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickWelcomeScreen(item)"
                    >
                        <v-list-item-title>Welcome screen...</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickSetActivelyWorking(item)"
                        v-if="search_state == 'pre-launch'"
                    >
                        <v-list-item-title>I am actively working this account</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickDeleteActivelyWorking(item)"
                        v-if="search_state == 'pre-launch'"
                    >
                        <v-list-item-title>Clear actively working this account</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickSetTrainingTime(item)"
                        v-if="search_state == 'pre-launch'"
                    >
                        <v-list-item-title>Set / change training time...</v-list-item-title>
                    </v-list-item>

                    <v-divider/>

                    <v-list-item
                        v-on:click="onClickSignInAs(item)"
                        :disabled="item.is_prospective_organization"
                    >
                        <v-list-item-title>Sign in as...</v-list-item-title>
                    </v-list-item>

                    <v-divider/>

                    <v-list-item
                        v-for="possible_value in archivedValues(item)"
                        :key="possible_value"
                        v-on:click="onArchiveOganization(item)"
                        :disabled="!$rc.$state.hasRedChirpStaffRoleFinance()"
                    >
                        <v-list-item-content>
                            <v-list-item-title v-text="possible_value"></v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickSetState(item, 'active')"
                        v-if="item.organization_state == 'pre-launch'"
                    >
                        <v-list-item-content>
                            <v-list-item-title>Move from pre-launch to active state</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onClickSetState(item, 'pre-launch')"
                        v-if="item.organization_state == 'active'"
                    >
                        <v-list-item-content>
                            <v-list-item-title>Return to pre-launch state</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-on:click="onChangeRevenueStatus(item)"
                        :disabled="!$rc.$state.hasRedChirpStaffRoleFinance()"
                    >
                        <v-list-item-title>Change Revenue Status...</v-list-item-title>
                    </v-list-item>

                     <v-list-item
                        v-on:click="onClickChangeTags(item)"
                    >
                        <v-list-item-title>Change Tags...</v-list-item-title>
                    </v-list-item>

                    <v-divider/>

                    <v-list-item
                        v-on:click="onClickVerifyNumbers(item)"
                        :disabled="item.is_prospective_organization"
                    >
                        <v-list-item-title>Verify All Phone Numbers...</v-list-item-title>
                    </v-list-item>

                    <v-divider/>

                     <v-list-item
                        v-on:click="onClickCreateOrganization(item)"
                        v-if="item.is_prospective_organization"
                    >
                        <v-list-item-title>Create Organization...</v-list-item-title>
                    </v-list-item>

                     <v-list-item
                        v-on:click="onClickOrganizationSetupWizard(item)"
                    >
                        <v-list-item-title>Organization Setup Wizard (OLD, soon to be removed)...</v-list-item-title>
                    </v-list-item>

                    <v-divider/>

                    <v-list-item
                        v-on:click="onClickProvideRedChirpPaymentMethod(item)"
                    >
                        <v-list-item-title>Public link: Provide new payment method...</v-list-item-title>
                    </v-list-item>

                </v-list>
            </v-menu>


        </template>

    </v-data-table>

    <m-archived-org-dialog
        ref="edit_archived_organization"
    />

    <m-admin-org-sign-in-as-dialog
        ref="admin-org-sign-in-as-dialog"
    />



    <m-edit-rc-crm-organization-dialog
        ref='edit-dialog'
        v-on:input="afterCreateOrg"
    />

    

</v-container>
</m-pageframe>
</template>

<script>
const { v4: uuidv4 } = require('uuid');
const _ = require('lodash');

export default {
    data() {
        return {
            search: '',
            results: [],
            filter_archived_accounts: null,
            filter_non_revenue_accounts: null,
            filter_prospective_organizations: null,

            search_integration: 'any_enum_value',
            search_industry: 'any_enum_value',
            search_state: 'any_enum_value',

            last_change_industry_item: null,

            sort_by: null,
            sort_desc: null,
            tag_filter: {'type': 'includes-any', 'values': []},
        }
    },

    mounted () 
    {
        this.search_state = this.$route.query.search_state;
        if ( this.search_state == null ) this.search_state = 'any_enum_value';

        if ( this.search_state == 'pre-launch' )
        {
            this.sort_by = 'time_training_date';
            this.sort_desc = false;
        }

        this.doUpdate();
    },

    methods: {

        update: _.debounce(function(){
            this.doUpdate();
        }, 751),
        
        doUpdate()
        {
            let search = {
                'is_archived' : this.filter_archived_accounts,
                'is_revenue_account' : this.filter_non_revenue_accounts,
                'full_text' : this.search,
                'is_prospective_organization': this.filter_prospective_organizations,
                'rc_tag_ids': this.tag_filter.values.length == 0 ? null : this.tag_filter,
            };

            if ( this.$rc.isValidEnumValue(this.search_industry,'industry_code') )
            {
                search.industry =  this.search_industry;
            }

            if ( this.$rc.isValidEnumValue(this.search_integration, 'integration_name') )
            {
                search.active_integrations = { 'type': 'contains', 'value' : this.search_integration };
            }

            if ( this.$rc.isValidEnumValue(this.search_state, 'organization_state')  )
            {
                search.organization_state =  this.search_state;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/red-chirp-crm/organizations',
        
                'params' : search,
        
                'on_success': (data) => {
                    
                        for ( let i = 0; i < data.results.length; i++ )
                        {
                            data.results[i] = this.setToDoCounts(data.results[i]);
                        }
                        
                        this.results = data.results;


                    },
        
                'on_error': 'Unable to search organizations',
            });
        },

        archivedValues(item)
        {
            let ret = [];

            if (item.is_archived)
            {
                if ( !item.is_prospective_organization ) 
                    ret.push('View / Edit Archived Notes');

                ret.push('Unarchive Organization');
            }
            else
            {
                ret.push('Archive Organization');
            }

            return ret;
        },

        onOrganizationSettings(item)
        {
            this.$router.push(
                { 
                    name: 'settings-organization', 
                    params: { 
                        'organization_id': item.organization_id,
                    } 
                }
            );
        },

        onClickSignInAs(item)
        {
            this.$refs['admin-org-sign-in-as-dialog'].showDialog(item);
        },

        onPayments(item)
        {
             this.$router.push({
                'name': 'search-payments',
                'params' : {
                    'organization_id' : item.organization_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        onPickups(item)
        {
             this.$router.push({
                'name': 'search-pickups',
                'params' : {
                    'organization_id' : item.organization_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        onChangeRevenueStatus(item)
        {
            this.$rc.confirm({
                'title': 'Confirm change revenue status',
                'body': item.is_revenue_account ? "Set account as test account?" : "Set account as revenue account?",
                'btn_text_ok': 'Set',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doChangeRevenueStatus(item);
                },
            });
        },

        onClickChangeTags(item)
        {
            this.$rc.change_tags_dialog({
            'organization_id': '53485c43-8d64-4be9-82fa-6ae2b4efe575', // RedChirp Corporate
            'applies_to': 'organization',
            'selected_tag_ids': item.rc_tag_ids,
            'show_archived': false,
            'on_save': (ids) => {
               
               this.$rc.rest({
                    'spinner': true,
                    'endpoint': 'PATCH: /api/v1/red-chirp-crm/organizations',
                
                    'params' : {
                            'organization_id': item.organization_id,
                            'rc_tag_ids': ids
                        },
                
                    'on_success': (data) => {
                            this.doUpdate();
                        },
                
                    'on_error': 'Unable to update organization',
                });
            }
        });
        },

        doChangeRevenueStatus(item)
        {
           this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/red-chirp-crm/organizations',
            
                'params' : {
                        'organization_id': item.organization_id,
                        'is_revenue_account': !item.is_revenue_account
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to update organization',
            });
        },

        onArchiveOganization(item)
        {
            if ( item.is_prospective_organization )
            {
                this.onArchiveProspectiveOrganization(item);
            }
            else
            {
                this.onArchiveCreatedOrganization(item);
            }
        },

        onClickSetState(item, new_state)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PATCH: /api/v1/organizations/organization-account',
            
                'params' : {
                    'organization_id': item.organization_id,
                    'organization_state': new_state,
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to change organization state.',
            });
        },

        onArchiveProspectiveOrganization(item)
        {
            this.$rc.rest({
                    'spinner': true,
                    'endpoint': 'PATCH: /api/v1/red-chirp-crm/organizations',
            
                    'params' : {
                            'organization_id': item.organization_id,
                            'is_archived': !item.is_archived
                        },
            
                    'on_success': (data) => {
                            this.doUpdate();
                        },
            
                    'on_error': 'Unable to update organization',
                });
        },

        onArchiveCreatedOrganization(item)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/organizations/archive-organization/:organization_id',
        
                'params' : {
                        'organization_id': item.organization_id,
                    },
        
                'on_success': (data) => {
                        this.$refs['edit_archived_organization'].showDialog(data, this.doUpdate);
                    },
        
                'on_error': 'Unable to get organization archived information',
            });
        },

        onClickCreateOrganization(item)
        {
            this.$router.push({
                'name': 'admin-create-organization',
                'params' : {
                    'organization_id': item.organization_id,
                }
            });
        },

        onClickOrganizationSetupWizard(item)
        {
            this.goToSetupWizard(item.organization_id);
        },

        goToSetupWizard(organization_id)
        {
            this.$router.push(
                { 
                    name: 'admin-organization-setup-wizard', 
                    params: { 
                        'organization_id': organization_id,
                    } 
                }
            );
        },

        onClickSearchContactRequests(item)
        {
            let organization_id = item.organization_id;

            this.$router.push({
                'name': 'search-inbox-contact-requests-without-start-inbox',
                'params' : {
                    'organization_id' : organization_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        onClickSearchContacts(item)
        {
            let organization_id = item.organization_id;

            this.$router.push({
                'name': 'fulltext-search-contacts',
                'params' : {
                    'organization_id' : organization_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        onClickVerifyNumbers(item)
        {
            let organization_id = item.organization_id;

            this.$rc.showSpinner();
            
            this.$rc.adminVerifyAllOrgNumbers(
                organization_id,
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.$rc.alert("Verification Started", "Started verifying "+data.size+' numbers.');
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to verify organization numbers.');
                },
            );
        },

        onClickExportToExcel()
        {
            this.$rc.excelExport(
                    'GET: /api/v1/admin/start-excel-export-organizations',
                    {
                    }
                );
        },

        onClickViewNotes(item)
        {
            this.$router.push({
                'name': 'rc-crm-view-organization',
                'params' : {
                    'organization_id': item.organization_id
                }
            });
        },

        onClickProvisionScreen(item)
        {
             this.$rc.newSignInAs({
                'organization_id': item.organization_id,
                'employee_id': 'any-org-admin', 
                'after_route': {
                    'name': 'provision',
                    'params' : {
                        'organization_id': item.organization_id,
                    },
                },
            });
        },

        onClickWelcomeScreen(item)
        {
            if ( item.any_org_admin_employee_id == null )
            {
                this.$rc.alert("No organization admin", "This organization does not have any organization admin accounts.  You need to create one before the welcome screen will be accessible");
                return;
            }

            this.$rc.newSignInAs({
                'organization_id': item.organization_id,
                'employee_id': 'any-org-admin', 
                'after_route': {
                    'name': 'welcome_ordinary_user',
                    'params' : {
                        'organization_id': item.organization_id,
                    },
                },
            });
        },

        afterCreateOrg(new_org)
        {
            this.$router.push({
                'name': 'rc-crm-view-organization',
                'params' : {
                    'organization_id': new_org.organization_id
                }
            });
        },

        onClickNewProspectiveOrganization()
        {
            this.$refs['edit-dialog'].showDialog();
        },

        onClickProvideRedChirpPaymentMethod(item)
        {    
            let url = this.$rc.linkProvideRedChirpPaymentMethod(this.$router, item.organization_id);

            this.$rc.copy_dialog({
                    'title': 'Provide new RedChirp payment method: '+item.label,
                    'text': 'Provide RedChirp Payment Method URL (public, no sign-in needed)',
                    'value': url,
                });

        },

        setToDoCounts(item)
        {
            item['count_todo_client'] = item.count_waiting_on_client == null ? null : item.count_waiting_on_client;

            {
                item['count_todo_redchirp'] = null;

                if ( item.count_pending != null && item.count_not_applicable != null && item.count_needs_confirmation != null )
                    item['count_todo_redchirp'] = item.count_pending + item.count_not_applicable + item.count_needs_confirmation;
            }

            return item;
        },
    
        onClickSnapshotWelcomeTasks()
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'GET: /api/v1/welcome/tasks/snapshot-welcome-tasks',
            
                'params' : {
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to update ToDo counts',
            });
        },

        styleLabel(item)
        {
            if ( this.search_state == 'pre-launch' )
            {
                let count = item.count_todo_redchirp;
                if ( count == null || count <= 0 ) return null;
                return 'font-weight: bold';
            }

            return null;
        },

        onClickSetTrainingTime(item)
        {
            this.$rc.promptTime({
                'value': item.time_training,
                'optional': true,
                'on_set': (time) => {
                    this.onSetTrainingTime(item, time);
                },
            });
        },

        onSetTrainingTime(item, time)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/admin/organization-admin-settings',
            
                'params' : {
                        'organization_id': item.organization_id,
                        'time_training': time,
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to set training time',
            });
        },

        prettyTrainingTime(item)
        {
            if ( item.time_training == null ) 
                return null;

            return this.$rc.pretty(item.time_training, 'date') + ' @ ' + this.$rc.pretty(item.time_training, 'time');
        },

        customSort(items, index, isDesc)
        {
            items.sort(
                (a,b) => {

                    if ( index == 'time_training_date' || index == 'time_training_time' )
                    {
                       let ret = this.$rc.compareNullsLast(a['time_training'],b['time_training'], isDesc[0]);
                       if ( ret == 0 ) ret = this.$rc.compareNullsLast(a['label'],b['label'], false);
                       return ret;
                    }

                    if (isDesc[0]) 
                    {
                        return a[index] < b[index] ? 1 : -1;
                    } 
                    else 
                    {
                        return a[index] < b[index] ? -1 : 1;
                    }
                },
            );

            return items;

        },

        onClickSetActivelyWorking(item)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/welcome/actively-working',
            
                'params' : {
                        'organization_id': item.organization_id,
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to set actively working this organization',
            });
        },

        onClickDeleteActivelyWorking(item)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'DELETE: /api/v1/welcome/actively-working',
            
                'params' : {
                        'organization_id': item.organization_id,
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to clear actively working this organization',
            });
        },

    },

    computed: {
        headers()
        {
            let ret = [];

            ret.push({ text: 'Tags', value: 'tags' });
            ret.push({ text: 'Label', value: 'label' });
            ret.push({ text: 'HQ Location', value: 'country_and_subdivision' });
            ret.push({ text: 'Industry', value: 'industry' });
            ret.push({ text: 'Campaign Id (Bandwidth)', value: 'default_tcr_campaign_id_bandwidth'});
            ret.push({ text: 'Timezone', value: 'primary_timezone' });
            ret.push({ text: 'State', value: 'organization_state' });
            ret.push({ text: 'Non-Revenue', value: 'is_revenue_account' });
            ret.push({ text: 'Promotion Prohibited', value: 'red_chirp_promotion_prohibited' });
            ret.push({ text: 'Integrations', value: 'integrations' });

            if ( this.search_state == 'pre-launch')
            {
                ret.push({ text: 'ToDo: Client', value: 'count_todo_client' });
                ret.push({ text: 'ToDo: RedChirp', value: 'count_todo_redchirp' });
                ret.push({ text: 'Campaign Registration Date', value: 'time_campaign_created'});
                ret.push({ text: 'Campaign Status', value: 'vetting_status' });
                ret.push({ text: 'Training Date', value: 'time_training_date' });
                ret.push({ text: 'Time', value: 'time_training_time' });
            }

            ret.push({ text: 'Action', value: 'action', align: 'center' });

            return ret;
        },
    },

    watch: {
        $route (to, from)
        {
            if ( to.query.search_state == 'pre-launch' && to.query.search_state != from.query.search_state )
            {
                // Reset the various search state variables...
                this.search = null;
                this.results = [];
                this.filter_archived_accounts = null;
                this.filter_non_revenue_accounts = null;
                this.filter_prospective_organizations = null;
            

            
                this.search_integration = 'any_enum_value';
                this.search_industry = 'any_enum_value';
                this.search_state = 'any_enum_value';

                this.last_change_industry_item = null;

                this.sort_by = null;
                this.sort_desc = null;

                this.search_state = this.$route.query.search_state;
                if ( this.search_state == null ) this.search_state = 'any_enum_value';

                if ( this.search_state == 'pre-launch' )
                {
                    this.sort_by = 'time_training_date';
                    this.sort_desc = false;
                }

                this.doUpdate();
            }
        },
    },
}
</script>

<style lang="scss" scoped>

.industry_cell:hover
{
    cursor: pointer;
    text-decoration: underline;
}

</style>